import type { FromModel, ModelDefinition } from "@/__main__/data-model.mjs";
import createModel, { Mapped, Silent, Tuple } from "@/__main__/data-model.mjs";

const COLUMN_COUNT = 7;
const COLUMNS = Array.from({ length: COLUMN_COUNT }, () => String);

const model = Tuple(COLUMNS) satisfies ModelDefinition;

const columnModel = {
  name: String,
  position: Number,
  typeName: String,
  typeText: String,
};

const Entry = {
  mean: Number,
  stdev: Number,
  count: Number,
};

const MinuteStats = Mapped({
  key: Number,
  value: {
    num_games: Number,
    win_rate: Number,
    stats: {
      kills: Entry,
      true_damage_dealt_to_champions: Entry,
      total_heal: Entry,
      gold_earned: Entry,
      total_heals_on_teammates: Entry,
      physical_damage_dealt_to_champions: Entry,
      double_kills: Entry,
      gold_spent: Entry,
      deaths: Entry,
      total_minions_killed: Entry,
      spell1_casts: Entry,
      spell2_casts: Entry,
      spell3_casts: Entry,
      spell4_casts: Entry,
      total_damage_dealt_to_champions: Entry,
      assists: Entry,
      penta_kills: Entry,
      vision_score: Entry,
      quadra_kills: Entry,
      triple_kills: Entry,
      time_c_cing_others: Entry,
      total_damage_shielded_on_teammates: Entry,
      time_played: Entry,
      damage_self_mitigated: Entry,
      damage_dealt_to_turrets: Entry,
      first_blood_kill: Entry,
      total_damage_taken: Entry,
      magic_damage_dealt_to_champions: Entry,
      wards_placed: Entry,
      wards_killed: Entry,

      effective_heal_and_shielding: Entry,
      enemy_champion_immobilizations: Entry,
      buffs_stolen: Entry,
      control_ward_time_coverage_in_river_or_enemy_half: Entry,
      damage_taken_on_team_percentage: Entry,
      enemy_missing_pings: Entry,
      immobilize_and_kill_with_ally: Entry,
      kill_participation: Entry,
      solo_kills: Entry,
      quick_solo_kills: Entry,
      scuttle_crab_kills: Entry,
      skillshots_dodged: Entry,
      skillshots_hit: Entry,
      team_damage_percentage: Entry,
      two_wards_one_sweeper_count: Entry,
      wards_guarded: Entry,
      ability_uses: Entry,
    },
  },
});

const ChampionByDurationModel = createModel({
  data: {
    executeDatabricksQuery: {
      payload: {
        manifest: {
          schema: {
            columnCount: Number,
            columns: [columnModel],
          },
        },
        result: {
          chunkIndex: Silent(Number),
          dataArray: [model],
          rowCount: Silent(Number),
          rowOffset: Silent(Number),
        },
      },
    },
  },
}).transform(MinuteStats, (data) => {
  const {
    data: { executeDatabricksQuery },
  } = data;

  const { columns = [] } =
    executeDatabricksQuery?.payload?.manifest?.schema || {};

  const list = executeDatabricksQuery?.payload?.result?.dataArray || [];

  const stats = list.map((s) => {
    const result = {
      patch: "",
      queue_id: null,
      champion_id: 0,
      championId: 0,
      individual_position: null,
      role: null,
      tier: null,
      region: null,
      stats_by_game_duration: {},
    };
    for (const column of columns) {
      const value: string | null = s[column.position] as unknown as string;
      result[column.name] = castType(column.typeName, value);
    }

    result.championId = result.champion_id;

    return result;
  });

  return stats[0]?.stats_by_game_duration;
});

export default ChampionByDurationModel;

export type ChampionByDurationStats = FromModel<typeof ChampionByDurationModel>;

function castType(typeText: string, value: string) {
  switch (typeText) {
    case "BOOLEAN":
      return value === "true";
    case "DOUBLE":
    case "BIGINT":
    case "LONG":
    case "INT":
      return value ? Number(value) : 0;
    case "MAP": {
      return value ? recursiveJsonParse(value) : {};
    }
    case "ARRAY":
      return value ? JSON.parse(value) : [];
    case "STRUCT":
      return value ? JSON.parse(value) : {};
    case "STRING":
    case undefined:
    default:
      return value ?? "";
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function recursiveJsonParse(input: any): any {
  // Base case 1: If input is null or undefined, return as is
  if (input === null || input === undefined) {
    return input;
  }

  // Base case 2: If input is a string, try to parse it
  if (typeof input === "string") {
    try {
      // Attempt to parse the string as JSON
      const parsed = JSON.parse(input);

      // If successfully parsed and result is an object or array,
      // recursively parse its contents
      if (parsed && (typeof parsed === "object" || Array.isArray(parsed))) {
        return recursiveJsonParse(parsed);
      }

      // If parsed result is a primitive, return it directly
      return parsed;
    } catch (_e) {
      // If parsing fails, it's not a valid JSON string, so return as is
      return input;
    }
  }

  // Case 3: If input is an array, recursively parse each element
  if (Array.isArray(input)) {
    return input.map((item) => recursiveJsonParse(item));
  }

  // Case 4: If input is an object, recursively parse each property value
  if (typeof input === "object") {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: Record<string, any> = {};

    for (const key in input) {
      if (Object.prototype.hasOwnProperty.call(input, key)) {
        result[key] = recursiveJsonParse(input[key]);
      }
    }

    return result;
  }

  // Base case 5: For all other types (number, boolean, etc.), return as is
  return input;
}
